import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Calendar, User, ArrowRight, BookOpen, CheckCircle, Mail, Clock } from 'lucide-react';
import { useToast } from '../ui/use-toast';
import axios from 'axios';

const BlogSection = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/api/newsletter`, { email });

      if (response.data.success) {
        setSubmittedEmail(email);
        setShowConfirmationModal(true);
        setEmail(''); // Clear form
      } else {
        throw new Error(response.data.message || 'Subscription failed');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Subscription Failed",
        description: error.response?.data?.message || "Please try again later",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const blogPosts = [
    {
      title: "How to Export Gold Legally from Africa in 2025",
      excerpt: "Complete guide to legal gold export processes, documentation requirements, and compliance standards for African artisanal miners.",
      date: "January 15, 2025",
      author: "GOLDCIF Team",
      readTime: "5 min read",
      slug: "legal-gold-export-africa-2025",
      tags: ["Legal Export", "Africa", "Compliance"],
      schema: {
        "@type": "BlogPosting",
        "headline": "How to Export Gold Legally from Africa in 2025",
        "description": "Complete guide to legal gold export processes, documentation requirements, and compliance standards for African artisanal miners.",
        "author": {
          "@type": "Organization",
          "name": "GOLDCIF"
        },
        "publisher": {
          "@type": "Organization", 
          "name": "GOLDCIF",
          "logo": {
            "@type": "ImageObject",
            "url": "/goldcif-logo.png"
          }
        },
        "datePublished": "2025-01-15",
        "dateModified": "2025-01-15"
      }
    },
    {
      title: "Uganda Gold Mining Regulations Guide 2025",
      excerpt: "Everything you need to know about Uganda's gold mining laws, licensing requirements, and export procedures for artisanal miners.",
      date: "January 12, 2025",
      author: "Legal Team",
      readTime: "7 min read", 
      slug: "uganda-gold-mining-regulations-2025",
      tags: ["Uganda", "Mining Laws", "Regulations"],
      schema: {
        "@type": "BlogPosting",
        "headline": "Uganda Gold Mining Regulations Guide 2025",
        "description": "Everything you need to know about Uganda's gold mining laws, licensing requirements, and export procedures for artisanal miners.",
        "author": {
          "@type": "Organization",
          "name": "GOLDCIF"
        },
        "publisher": {
          "@type": "Organization",
          "name": "GOLDCIF", 
          "logo": {
            "@type": "ImageObject",
            "url": "/goldcif-logo.png"
          }
        },
        "datePublished": "2025-01-12",
        "dateModified": "2025-01-12"
      }
    },
    {
      title: "Tax Compliance for Artisanal Gold Miners",
      excerpt: "Understanding export taxes, customs duties, and compliance requirements when selling gold internationally. GOLDCIF covers all fees.",
      date: "January 10, 2025", 
      author: "Tax Advisory",
      readTime: "6 min read",
      slug: "tax-compliance-artisanal-gold-miners",
      tags: ["Tax Compliance", "Export Fees", "Documentation"],
      schema: {
        "@type": "BlogPosting",
        "headline": "Tax Compliance for Artisanal Gold Miners", 
        "description": "Understanding export taxes, customs duties, and compliance requirements when selling gold internationally.",
        "author": {
          "@type": "Organization",
          "name": "GOLDCIF"
        },
        "publisher": {
          "@type": "Organization",
          "name": "GOLDCIF",
          "logo": {
            "@type": "ImageObject", 
            "url": "/goldcif-logo.png"
          }
        },
        "datePublished": "2025-01-10",
        "dateModified": "2025-01-10"
      }
    }
  ];

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "GOLDCIF Blog - Legal Gold Export Insights",
    "description": "Expert insights on legal gold export, mining regulations, and compliance for African artisanal miners",
    "url": `${window.location.origin}/#blog`,
    "publisher": {
      "@type": "Organization",
      "name": "GOLDCIF"
    },
    "blogPost": blogPosts.map(post => post.schema)
  };

  return (
    <section id="blog" className="py-24 bg-[#020817]">
      {/* Schema.org JSON-LD for Blog */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <BookOpen className="h-10 w-10 text-[#D4AF37] mr-4" />
            <h2 className="text-4xl font-bold text-white">Gold Export Insights</h2>
          </div>
          <h3 className="text-xl text-white/70 max-w-3xl mx-auto">
            Expert guidance on legal gold export, mining regulations, and compliance for African artisanal miners
          </h3>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-500 border-0 bg-[#0B132B]/60 hover:scale-105 transform cursor-pointer">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-2 text-sm text-white/50 mb-3">
                  <Calendar className="h-4 w-4" />
                  <span>{post.date}</span>
                  <span>•</span>
                  <User className="h-4 w-4" />
                  <span>{post.author}</span>
                </div>
                <CardTitle className="text-xl font-bold text-white group-hover:text-[#0B132B] transition-colors leading-tight">
                  {post.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-white/70 mb-4 leading-relaxed">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.slice(0, 2).map((tag, tagIndex) => (
                      <span 
                        key={tagIndex} 
                        className="px-2 py-1 bg-[#D4AF37]/10 text-white text-xs rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center text-[#D4AF37] group-hover:text-[#C5A059] transition-colors">
                    <span className="text-sm font-medium mr-1">{post.readTime}</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-[#040F2D]/10 to-[#D4AF37]/10 rounded-2xl p-8 border border-[#D4AF37]/20">
            <h4 className="text-2xl font-bold text-white mb-4">Stay Updated</h4>
            <p className="text-white/80 mb-6">
              Get the latest insights on legal gold export processes, regulations, and market updates directly to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-12 text-lg bg-[#0B132B]/60 border-2 border-gray-300 focus:border-[#040F2D]"
                disabled={isSubmitting}
                required
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="bg-[#D4AF37] hover:bg-[#C5A059] text-white px-6 py-3 h-12 font-semibold transition-all transform hover:scale-105"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>

          {/* Newsletter Confirmation Modal */}
          <Dialog open={showConfirmationModal} onOpenChange={setShowConfirmationModal}>
            <DialogContent className="max-w-md mx-4 sm:max-w-lg">
              <DialogHeader className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <DialogTitle className="text-2xl font-bold text-white">
                  Successfully Subscribed!
                </DialogTitle>
                <DialogDescription className="text-lg text-white/70 space-y-4">
                  <div className="space-y-3">
                    <p className="font-semibold">
                      Thank you for subscribing to GOLDCIF updates!
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center text-blue-700 mb-2">
                        <Mail className="h-5 w-5 mr-2" />
                        <span className="font-semibold">Check Your Email</span>
                      </div>
                      <p className="text-blue-600 text-sm">
                        A welcome email has been sent to <strong>{submittedEmail}</strong>
                      </p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center text-yellow-700 mb-2">
                        <Clock className="h-5 w-5 mr-2" />
                        <span className="font-semibold">What's Next</span>
                      </div>
                      <p className="text-yellow-600 text-sm">
                        You'll receive regular updates about gold export opportunities, regulations, and market insights.
                      </p>
                    </div>
                  </div>
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex justify-center pt-4">
                <Button 
                  onClick={() => setShowConfirmationModal(false)}
                  className="bg-gradient-to-r from-[#040F2D] to-[#0B132B] hover:from-[#0B132B] hover:to-[#040F2D] text-white px-8 py-2"
                >
                  Got it
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;